import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DOCUMENT, ElementRef, OnInit, Renderer2, afterNextRender, computed, effect, inject, input, output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DynamicHeaderCommon } from 'src/app/components/dynamic-header/category/dynamic-header-common.class';
import { DynamicMenuCollapseComponent } from "src/app/components/dynamic-menu-collapse/dynamic-menu-collapse.component";
import { SearchBarComponent } from "src/app/components/search-suggestion/components/search-bar/search-bar.component";
import { StaticMenuCollapseComponent } from "src/app/components/static-menu-collapse/static-menu-collapse.component";
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';
import { Cart } from 'src/app/core/services/cart/cart-api.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { createDefaultCart, createDefaultHTMLElement, createDefaultHeaderData } from 'src/app/core/utilities/type-defaults.util';
import { Category } from 'src/app/shared/services/common-api.service';
import { MenuCollapseService } from '../../services/menu-collapse.service';
import { HeaderData } from '../header-data';

@Component({
    selector: 'category-layout-10',
    templateUrl: './layout-10.component.html',
    styleUrls: ['./layout-10.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, RouterModule, SearchBarComponent, DynamicMenuCollapseComponent, StaticMenuCollapseComponent]
})
export class Layout10Component extends DynamicHeaderCommon implements OnInit, AfterViewInit {
    protected override userService = inject(UserService);
    private readonly deviceDetectionService = inject(DeviceDetectionService);

    readonly dynamicHeaderInline = input<boolean>(false);
    readonly dynamicHeaderHover = input<boolean>(false);
    readonly layoutTypeInput = input<string>("");
    readonly domain = input<string>('');
    readonly headerDataInput = input<HeaderData>(createDefaultHeaderData());

    readonly cartInput = input<Cart | null>(null);
    readonly categoryInput = input<Category[] | null>(null);
    readonly clickedElementAvailableInput = input<HTMLElement | null>(null);

    cart = computed(() => this.cartInput() || createDefaultCart());
    layoutType = computed(() => this.layoutTypeInput());
    category = computed(() => this.categoryInput() || []);
    clickedElementAvailable = computed(() => this.clickedElementAvailableInput() || (createDefaultHTMLElement() as HTMLElement));

    // Override getter for backward compatibility with base class
    override get headerData(): HeaderData {
        return this.headerDataInput();
    }


    // Expose service signals for template usage
    get isDesktop() {
        return this.deviceDetectionService.isDesktop;
    }
    get isMobile() {
        return this.deviceDetectionService.isMobile;
    }
    get isTablet() {
        return this.deviceDetectionService.isTablet;
    }
    get deviceType() {
        return this.deviceDetectionService.deviceType;
    }

    // Update outputs to use Angular 20 output() function
    logout$ = output<boolean>();
    categoryHeaderStyle$ = output<string>();
    override searchQuery$ = output<{ keyboardEvent: KeyboardEvent, suggestionItem: any, searchInputValue?: string }>();

    isSubmenuAvailable = true;
    override showSubcategoryPanel = false;
    override searchInputControl = new FormControl<string | null>('');

    constructor() {
        const browserApiService = inject(BrowserApiService);

        super(
            inject(Renderer2),
            inject(MenuCollapseService),
            inject(ElementRef),
            inject(PlatformService),
            inject<Document>(DOCUMENT),
            browserApiService
        );

        effect(() => {
            const headerDataValue = this.headerDataInput();
            this.addHoverForMenu();
        });

        // âœ… Angular 20: When category data is available, attach hover listeners
        effect(() => {
            const categoryValue = this.categoryInput();
            if (categoryValue && categoryValue.length > 0) {
                afterNextRender(() => {
                    this.addHoverForMenu();
                });
            }
        });
    }

    ngOnInit(): void {
        if (this.platformService.isBrowser()) {
            // âœ… REMOVED: setTimeout - now handled by categoryInput effect in constructor
        }
    }

    ngAfterViewInit(): void {
        if (this.platformService.isBrowser()) {
            this.repositionHeaderMenu();
            this.applyFocusEvent();
        }
    }

    // Additional methods specific to Layout 10
    onLinkClick(event: Event): void {
        const subMenus = document.getElementsByClassName('hide-sub-menu-header');
        // Iterate over the elements and set opacity to 0
        Array.from(subMenus).forEach((subMenu) => {
            this.renderer.setStyle(subMenu, 'display', 'none');
        });
    }

    override toggleSearchDropDown() {
        this.isSearchOverlay = !this.isSearchOverlay;
        this.isHamburgerMenu = false;
        this.searchInputControl.reset();
        if (this.isSearchOverlay) {
            this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
        } else {
            this.renderer.removeStyle(this.document.body, 'overflow');
        }
    }

    override toggleHamburgerMenu() {
        this.isToggled = true;
        this.menuCollapseService.toggleHamburgerMenu();
        this.isHamburgerMenu = !this.isHamburgerMenu;
        this.isSearchOverlay = false;
        if (this.isHamburgerMenu) {
            this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
        } else {
            this.renderer.removeStyle(this.document.body, 'overflow');
        }
    }

    searchItems() {
        this.toggleSearchDropDown();
    }

    subMenu() {
        this.isSubmenuAvailable = true;
    }

    openSubcategoryPanel(sub_categories: Category[] = []) {
        if (sub_categories?.length) {
            this.showSubcategoryPanel = !this.showSubcategoryPanel;
            this.mobileSubCategory = sub_categories;
        }
        return;
    }

    // Implement searchProduct method to handle search input events
    override searchProduct(searchQuery: { keyboardEvent: KeyboardEvent, suggestionItem: any, searchInputValue?: string }): void {
        const { keyboardEvent, suggestionItem } = searchQuery;

        if (suggestionItem) {
            this.searchQuery$.emit(searchQuery);
            this.disableSearchPanel(); // Close panel after suggestion selection
        } else if (keyboardEvent) {
            if (keyboardEvent.key === 'Enter') {
                this.searchQuery$.emit({ ...searchQuery, searchInputValue: this.searchInputControl?.value || '' });
                this.disableSearchPanel(); // Close panel after Enter key press
            }
        }
    }
}
